import { addDays, addHours, differenceInDays, format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ampli } from '../../../../ampli';
import { MainHead } from '../../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { browserStorageKeys } from '../../../../constants/browserStorageKeys/browserStorageKeys.constant';
import { flarelaneEventKeys } from '../../../../constants/flarelaneEvent/flarelaneEvent.contant';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { usePatchOpenMarketProductViewsByIdMutation } from '../../../../hooks/api/product/mutations/usePatchOpenMarketProductViewsById.mutation';
import { usePostBumpUserSellTractorMutation } from '../../../../hooks/api/product/mutations/usePostBumpUserSellTractor.mutation';
import { useGetOpenMarketProductByIdQuery } from '../../../../hooks/api/product/queries/useGetOpenMarketProductById.query';
import { useGetTodaySellTractorBumpStatus } from '../../../../hooks/api/product/queries/useGetTodaySellTractorBumpStatus.query';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IOpenMarketProduct, IOpenMarketProductDetail } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { OpenMarketProductSummaryView } from '../../../../views/Product/detailView/OpenMarketProductSummaryView/OpenMarketProductSummaryView';
import { DirectDealDetailPageFooter } from './components/DirectDealDetailPageFooter';
import { DirectDealRequestConsultButton } from './components/DirectDealRequestConsultButton';

export const DirectDealDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const [isAmpliEventCalled, setIsAmpliEventCalled] = useState<boolean>(false);
  const [shouldOpenPriceSuggestModal, setShouldOpenPriceSuggestModal] = useState<boolean>(false);
  const Popup = usePopup();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const machineType = searchParams.get('machineType');

  const { data: product, isLoading: isGetProductQueryLoading } = useGetOpenMarketProductByIdQuery({
    productId: productId!,
  });

  const { mutate: patchOpenMarketProductById } = usePatchOpenMarketProductViewsByIdMutation();

  const handlePriceSuggestModalOpen = () => {
    if (!product) return;

    const productTitle = getOpenMarketProductTitle({
      brandName: product.machine?.brand?.name,
      machine: product.machine,
    });
    const brandName = product.machine?.brand?.name;
    const modelName = product.machine?.model;

    ampli.pvmClickOdpSuggestPriceButton({ brand: brandName, model: modelName, title: productTitle });

    // OpenMarketProductSummaryView의 가격 제안 모달을 열기 위해 상태를 설정
    setShouldOpenPriceSuggestModal(true);
  };

  const { openNotification } = useNotification();
  const { data: todaySellTractorBumpStatus, isLoading: isGetTodaySellTractorBumpStatusLoading } =
    useGetTodaySellTractorBumpStatus();

  const { mutate: postBumpUserSellTractor, isLoading: isPostBumpUserSellTractorLoading } =
    usePostBumpUserSellTractorMutation();

  const [isBumpModalVisible, setIsBumpModalVisible] = useState<boolean>(false);
  const [isNewProductCannotBumpModalVisible, setIsNewProductCannotBumpModalVisible] = useState<boolean>(false);
  const [isBumpDailyLimitOverModalVisible, setIsBumpDailyLimitOverModalVisible] = useState<boolean>(false);
  const [isBumpLimitPerTractorOverModalVisible, setIsBumpLimitPerTractorOverModalVisible] = useState<boolean>(false);
  const [isCanBumpAgainTimeModalVisible, setIsCanBumpAgainTimeModalVisible] = useState<boolean>(false);

  const handleBumpButtonClick = useCallback(
    (product: IOpenMarketProduct) => {
      if (!product) {
        return;
      }

      // approvalDate 기준 8일이 지나지 않았으면 끌어올리기 불가
      const bumpAvailableDate = addDays(new Date(product.approvalDate), 8);
      if (new Date() < bumpAvailableDate) {
        setIsNewProductCannotBumpModalVisible(true);
        return;
      }

      if (product.bump && product.bump.bumpTimes >= 5) {
        setIsBumpLimitPerTractorOverModalVisible(true);
        return;
      }

      if (
        product.bump &&
        product.bump?.lastBumpAt &&
        differenceInDays(new Date(), new Date(product.bump.lastBumpAt)) <= 6
      ) {
        setIsCanBumpAgainTimeModalVisible(true);
        return;
      }

      if (todaySellTractorBumpStatus?.todayBumpCount && todaySellTractorBumpStatus.todayBumpCount >= 1) {
        setIsBumpDailyLimitOverModalVisible(true);
        return;
      }
      setIsBumpModalVisible(true);
    },
    [todaySellTractorBumpStatus],
  );

  const bumpUserSellTractor = () => {
    postBumpUserSellTractor(
      {
        productId: product!.id,
        isAutoBumped: false,
      },
      {
        onSuccess: () => {
          openNotification({ message: '끌올이 완료 되었어요!' });
        },
        onError: (error) => {
          openNotification({ message: '끌어올리기 실패', type: 'error' });
        },
        onSettled: () => {
          closeBumpModal();
        },
      },
    );
  };

  const closeBumpModal = () => {
    setIsBumpModalVisible(false);
  };

  useEffect(() => {
    if (!product) {
      return;
    }

    if (product.status !== 'ONSALE') {
      navigate('/open-market/list');
      return;
    }

    const isMachineTypeMismatch = !machineType || machineType !== product.machine.machineType;
    if (isMachineTypeMismatch) {
      searchParams.set('machineType', product.machine.machineType);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, [product, navigate, location.pathname, machineType, searchParams]);

  useEffect(() => {
    if (product && productId && !isAmpliEventCalled) {
      patchOpenMarketProductById({ productId, reqBody: { views: product.views + 1 } });

      ampli.마켓오픈마켓매물상세페이지조회({
        '게시글 제목': getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }),
        '매물 코드': product.code,
      });

      if (window.ReactNativeWebView) {
        postMessageToRN({
          messageKey: 'flarelane_trackEvent',
          messageParam: flarelaneEventKeys.pvm_visit_odp,
          messageData: {
            openMarketProductDetailPageURL: window.location.href,
          },
        });
      }

      setIsAmpliEventCalled(true);
    }
  }, [product, productId, patchOpenMarketProductById, isAmpliEventCalled]);

  useEffect(() => {
    const visitedCount = browserStorage.getItem({
      storageType: 'localStorage',
      itemKey: browserStorageKeys.visitedCountODP,
    });

    const newCount = visitedCount ? Number(visitedCount) + 1 : 1;
    browserStorage.setItem({
      storageType: 'localStorage',
      itemKey: browserStorageKeys.visitedCountODP,
      value: newCount.toString(),
    });
  }, [location.pathname]);

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true, directlyPassedURL: pageInfo.openMarketListPage.path }} />,
    fixedFooter: (
      <DirectDealDetailPageFooter>
        <DirectDealRequestConsultButton
          product={product!}
          onPriceSuggestClick={handlePriceSuggestModalOpen}
          handleBumpButtonClick={(product: IOpenMarketProductDetail) => handleBumpButtonClick(product)}
        />
      </DirectDealDetailPageFooter>
    ),
    fixedHeaderHeight: 48,
    fixedFooterHeight: 0,
  });

  if (isGetProductQueryLoading) return <AnimateLoading />;

  if (product) {
    return (
      <>
        <Helmet>
          <title>{`${getOpenMarketProductTitle({
            brandName: product.machine?.brand?.name,
            machine: product.machine,
          })} - Pluva Market`}</title>
        </Helmet>

        <OpenMarketProductSummaryView
          product={product}
          onPriceSuggestClick={handlePriceSuggestModalOpen}
          shouldOpenPriceSuggestModal={shouldOpenPriceSuggestModal}
          onPriceSuggestModalOpened={() => setShouldOpenPriceSuggestModal(false)}
        />

        <PopupConfirmModal
          isShow={isBumpModalVisible}
          hasCloseButton={false}
          title={'🚀 주목받고 싶다면 지금 끌올!'}
          description={'끌올하면 상위에 재등록 돼요!\n지금 끌어올릴까요?\n(매물당 5회 가능)'}
          buttonArray={[
            {
              text: '끌어올리기',
              onClick: bumpUserSellTractor,
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: closeBumpModal,
              color: 'gray',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isBumpDailyLimitOverModalVisible}
          hasCloseButton={false}
          title={'☝🏻 끌올은 하루에 한대만!'}
          description={'할당된 횟수를 모두 사용 했어요.\n내일 다시 시도 해보세요!'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                setIsBumpDailyLimitOverModalVisible(false);
                ampli.pvmClickBumpOncePerDayModalButton();
                postMessageToRN({
                  messageKey: 'flarelane_trackEvent',
                  messageParam: flarelaneEventKeys.pvm_fail_bump,
                });
              },
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isNewProductCannotBumpModalVisible}
          hasCloseButton={false}
          title={'😓 아직 끌올을 사용할 수 없어요'}
          description={`등록 후 일주일 뒤에 가능해요!\n${format(
            addHours(addDays(new Date(product.approvalDate), 8), 1),
            'M월 d일(EEEE) HH시',
            { locale: ko },
          )}`}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                setIsNewProductCannotBumpModalVisible(false);
                ampli.pvmClickBumpAlreadyBumpModalButton();
              },
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isBumpLimitPerTractorOverModalVisible}
          hasCloseButton={false}
          title={'🙌🏻 끌올을 5회 사용했어요!'}
          description={'이 매물에 사용할 수 있는\n끌올을 모두 사용하셨어요.\n(매물당 5회 가능)'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                setIsBumpLimitPerTractorOverModalVisible(false);
                ampli.pvmClickBumpUsedAllAllottedAtteptsModalButton();
              },
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isCanBumpAgainTimeModalVisible}
          hasCloseButton={false}
          title={'🙂 이미 끌올 했어요!'}
          description={`다음 끌어올리기는\n${
            product.bump?.lastBumpAt
              ? format(addDays(new Date(product.bump?.lastBumpAt), 7), 'M월 d일(EEEE)', { locale: ko })
              : ''
          }에 가능해요!`}
          buttonArray={[
            {
              text: '확인',
              onClick: () => setIsCanBumpAgainTimeModalVisible(false),
              color: 'blue',
            },
          ]}
        />
      </>
    );
  }

  return null;
};
